import { DEG, type Point, type Rect } from '../../doc/geometry';
import type { Geometry } from '../../doc/schema';

/** Which edge/corner a handle sits on: -1 = left/top, 0 = middle, 1 = right/bottom. */
export interface Handle {
  sx: -1 | 0 | 1;
  sy: -1 | 0 | 1;
}

export const RESIZE_HANDLES: Handle[] = [
  { sx: -1, sy: -1 },
  { sx: 0, sy: -1 },
  { sx: 1, sy: -1 },
  { sx: 1, sy: 0 },
  { sx: 1, sy: 1 },
  { sx: 0, sy: 1 },
  { sx: -1, sy: 1 },
  { sx: -1, sy: 0 },
];

export const CORNER_HANDLES: Handle[] = RESIZE_HANDLES.filter((h) => h.sx !== 0 && h.sy !== 0);

/**
 * Resize a (possibly rotated) box by dragging one handle. The opposite edge/corner stays fixed.
 * Works in the item's local frame so rotation just falls out of the math.
 */
export function resizeGeometry(
  start: Geometry,
  handle: Handle,
  pointer: Point,
  opts: { keepAspect: boolean; minSize: number },
): Geometry {
  const rad = start.rotation * DEG;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const cx = start.x + start.w / 2;
  const cy = start.y + start.h / 2;
  const dx = pointer.x - cx;
  const dy = pointer.y - cy;
  // pointer in local (unrotated) coordinates, relative to the centre
  const lx = dx * cos + dy * sin;
  const ly = -dx * sin + dy * cos;
  // anchor = opposite side, in local coordinates
  const ax = (-handle.sx * start.w) / 2;
  const ay = (-handle.sy * start.h) / 2;

  let w = handle.sx ? Math.max(opts.minSize, handle.sx * (lx - ax)) : start.w;
  let h = handle.sy ? Math.max(opts.minSize, handle.sy * (ly - ay)) : start.h;

  if (opts.keepAspect && start.w > 0 && start.h > 0) {
    const ratio = start.w / start.h;
    if (handle.sx && handle.sy) {
      if (w / start.w > h / start.h) h = w / ratio;
      else w = h * ratio;
    } else if (handle.sx) {
      h = w / ratio;
    } else {
      w = h * ratio;
    }
  }

  // new centre in local coordinates: anchor plus half the new extent towards the handle
  const ncx = handle.sx ? ax + (handle.sx * w) / 2 : 0;
  const ncy = handle.sy ? ay + (handle.sy * h) / 2 : 0;
  const wx = cx + ncx * cos - ncy * sin;
  const wy = cy + ncx * sin + ncy * cos;
  return { x: wx - w / 2, y: wy - h / 2, w, h, rotation: start.rotation };
}

export function rotateGeometry(
  start: Geometry,
  startPointer: Point,
  pointer: Point,
  snapDegrees: number | null,
): number {
  const cx = start.x + start.w / 2;
  const cy = start.y + start.h / 2;
  const a0 = Math.atan2(startPointer.y - cy, startPointer.x - cx);
  const a1 = Math.atan2(pointer.y - cy, pointer.x - cx);
  let deg = start.rotation + (a1 - a0) / DEG;
  if (snapDegrees) deg = Math.round(deg / snapDegrees) * snapDegrees;
  deg = ((deg + 180) % 360 + 360) % 360 - 180;
  return Math.round(deg * 100) / 100;
}

/** Uniform scale of several items about the corner opposite the dragged one. */
export function scaleGroup(
  starts: ReadonlyMap<string, Geometry>,
  bbox: Rect,
  handle: Handle,
  pointer: Point,
  minSize: number,
): Record<string, Partial<Geometry>> {
  const ax = handle.sx === 1 ? bbox.x : bbox.x + bbox.w;
  const ay = handle.sy === 1 ? bbox.y : bbox.y + bbox.h;
  const sx = bbox.w > 0 ? ((pointer.x - ax) * handle.sx) / bbox.w : 1;
  const sy = bbox.h > 0 ? ((pointer.y - ay) * handle.sy) / bbox.h : 1;
  let s = Math.max(sx, sy);
  // don't let the smallest item collapse
  let smallest = Infinity;
  for (const g of starts.values()) smallest = Math.min(smallest, g.w, g.h);
  if (smallest !== Infinity) s = Math.max(s, minSize / smallest);
  s = Math.max(s, 0.01);

  const out: Record<string, Partial<Geometry>> = {};
  for (const [id, g] of starts) {
    out[id] = {
      x: ax + (g.x - ax) * s,
      y: ay + (g.y - ay) * s,
      w: g.w * s,
      h: g.h * s,
    };
  }
  return out;
}
